import { Directive, ElementRef, type OnDestroy, type OnInit, inject, input, output } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { fromEvent, type Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
    selector: '[webTabHeaderWidth]',
})
export class TabHeaderWidthDirective implements OnInit, OnDestroy {
    public readonly widthValue = input.required<number>();
    public readonly widthUnit = input.required<string>();
    public readonly rootElementId = input.required<string>();
    public readonly headerElementId = input.required<string>();
    public readonly bodyElementId = input.required<string>();
    public readonly isRight = input<boolean>(false);

    public readonly widthChange = output<number>();
    public readonly dragStart = output<void>();
    public readonly dragEnd = output<void>();

    private readonly MIN_WIDTH_PX = 40;
    private readonly MIN_WIDTH_PERCENT = 5;
    private element: HTMLElement;
    private subscriptions: Subscription[] = [];

    private readonly elementRef: ElementRef = inject(ElementRef);
    private readonly document = inject<Document>(DOCUMENT);

    public ngOnInit(): void {
        this.element = this.elementRef.nativeElement;
        this.initDrag();
    }

    private initDrag(): void {
        const dragStart$ = fromEvent<MouseEvent>(this.element, 'mousedown');
        const dragEnd$ = fromEvent<MouseEvent>(this.document, 'mouseup');
        const drag$ = fromEvent<MouseEvent>(this.document, 'mousemove').pipe(takeUntil(dragEnd$));
        let dragSub: Subscription;
        let dragEndSub: Subscription;
        let initialX: number;
        let initialWidthPx: number;
        let containerWidthPx: number;

        const dragStartSub = dragStart$.subscribe((dragStartEvent: MouseEvent) => {
            dragStartEvent.preventDefault();
            this.element.classList.add('dragging');
            this.dragStart.emit();

            const rootEl = this.document.getElementById(this.rootElementId());
            const headerEl = this.document.getElementById(this.headerElementId());
            if (!rootEl || !headerEl) {
                return;
            }

            initialX = dragStartEvent.clientX;
            initialWidthPx = headerEl.getBoundingClientRect().width;
            containerWidthPx = rootEl.getBoundingClientRect().width;

            dragSub = drag$.subscribe((dragEvent: MouseEvent) => {
                dragEvent.preventDefault();
                const delta = dragEvent.clientX - initialX;
                const nextPx = this.isRight() ? initialWidthPx - delta : initialWidthPx + delta;
                const safePx = Math.max(this.MIN_WIDTH_PX, nextPx);

                if (this.widthUnit() === '%') {
                    const rawPercent = (safePx / containerWidthPx) * 100;
                    const clampedPercent = Math.max(this.MIN_WIDTH_PERCENT, Math.min(95, rawPercent));
                    this.widthChange.emit(clampedPercent);
                    return;
                }

                this.widthChange.emit(Math.round(safePx));
            });

            dragEndSub = dragEnd$.subscribe(() => {
                if (dragSub) {
                    this.element.classList.remove('dragging');
                    dragSub.unsubscribe();
                    dragEndSub.unsubscribe();
                    this.dragEnd.emit();
                }
            });

            // Track the per-drag subscriptions so they are cleaned up if the directive
            // is destroyed mid-drag (they are undefined until the drag actually starts).
            this.subscriptions.push(dragSub, dragEndSub);
        });

        this.subscriptions.push(dragStartSub);
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach((subscription: Subscription) => subscription?.unsubscribe());
    }
}
